import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const PATH = "https://fenny.se/videoproduktion-sverige";

const CITY_LINKS = [
  { label: "Stockholm", path: "/videoproduktion/stockholm" },
  { label: "Göteborg", path: "/videoproduktion/goteborg" },
  { label: "Malmö", path: "/videoproduktion/malmo" },
  { label: "Västerås", path: "/videoproduktion/vasteras" },
];

const FAQ = [
  {
    q: "Vad är skillnaden mellan video- och filmproduktion?",
    a: "I praktiken samma sak. Video brukar referera till kortare, kanal-anpassat innehåll för web och sociala medier. Film används oftare för längre, mer regisserat material som reklamfilm och dokumentär. Fenny levererar båda i samma kedja.",
  },
  {
    q: "Vad kostar videoproduktion i Sverige?",
    a: "Social-content: 40 000–120 000 kr. Reklamfilm 15–30 s: 150 000–450 000 kr. Pulse-abonnemang för löpande videoflöde: från 14 995 kr/mån. Samma pris oavsett svensk ort.",
  },
  {
    q: "Levererar Fenny videoproduktion i hela Sverige?",
    a: "Ja. Storstadsregionerna har lokala team; övriga svenska städer löser vi med närmaste team eller distansproduktion. Pre-prod, klipp och godkännanden sker digitalt.",
  },
  {
    q: "Vilka kanaler optimerar ni för?",
    a: "LinkedIn, Instagram, TikTok, YouTube, paid social (Meta/Google), web-hero, mässa/event-skärmar. En master per kanal ingår; fler cutdowns offereras transparent.",
  },
  {
    q: "Hur snabbt kan ni starta?",
    a: "Första möte inom 5 arbetsdagar. Pulse-kunder har första klippet ute inom 10–14 dagar. Akut kampanj — sätter vi team inom en vecka.",
  },
];

export const Route = createFileRoute("/videoproduktion-sverige")({
  head: () => ({
    meta: [
      { title: "Videoproduktion Sverige — abonnemang & projekt | Fenny" },
      {
        name: "description",
        content:
          "Videoproduktion i hela Sverige för B2B, marknad och säljteam. Pulse-abonnemang från 14 995 kr/mån eller fast projektpris. Lokala team i tre storstäder, leverans i hela landet.",
      },
      { property: "og:title", content: "Videoproduktion Sverige | Fenny" },
      {
        property: "og:description",
        content:
          "Premiumvideo för svenska företag — kanal-anpassat innehåll till fast pris. Pulse från 14 995 kr/mån.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PATH },
    ],
    links: [{ rel: "canonical", href: PATH }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/videoproduktion-sverige", name: "Videoproduktion Sverige — abonnemang & projekt | Fenny", description: "Videoproduktion i hela Sverige för B2B, marknad och säljteam. Pulse-abonnemang från 14 995 kr/mån eller fast projektpris. Lokala team i tre storstäder, leverans i hela landet."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "Videoproduktion Sverige",
            path: PATH,
            description:
              "Fenny producerar video för svenska företag — abonnemang (Pulse) eller fast projektpris (Prime). Kanal-anpassat för LinkedIn, Instagram, TikTok, YouTube och paid social.",
            serviceType: "Videoproduktion",
            priceRange: "Från 14 995 kr/mån (Pulse) eller 60 000 kr/projekt",
            areaServed: ["Sweden"],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Videoproduktion", path: "/videoproduktion-pa-abonnemang" },
            { name: "Sverige", path: PATH },
          ]),
        ),
      },
    ],
  }),
  component: SwedenHub,
});

function SwedenHub() {
  return (
    <>
      <Section>
        <div className="mb-4 text-xs uppercase tracking-[0.18em] text-muted-foreground">
          Videoproduktion i Sverige
        </div>
        <h1 className="text-balance text-4xl font-semibold leading-[1.05] md:text-6xl">
          Videoproduktion i hela Sverige — kanal-anpassat, fast pris
        </h1>
        <QuickAnswer
          text="Fenny producerar video för svenska företag i hela landet. Kanal-anpassat content för LinkedIn, Instagram, TikTok, YouTube och paid social, levererat som abonnemang (Pulse från 14 995 kr/mån) eller fast projektpris (Prime från 60 000 kr). Lokala team i Stockholm, Göteborg och Malmö — distansproduktion på övriga svenska orter."
        />
      </Section>

      <Section>
        <h2 className="text-3xl font-semibold md:text-4xl">Städer med dedikerad sida</h2>
        <ul className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {CITY_LINKS.map((c) => (
            <li key={c.path}>
              <Link
                to={c.path}
                className="group flex items-center justify-between rounded-xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3 text-sm transition hover:border-foreground/30 hover:bg-foreground/[0.06]"
              >
                <span>Videoproduktion {c.label}</span>
                <ArrowRight className="size-4 opacity-50 transition group-hover:translate-x-0.5 group-hover:opacity-100" />
              </Link>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-foreground/60 text-sm">
          Saknas er ort? Vi producerar på alla större svenska orter — kontakta
          oss så får ni ett fast pris.
        </p>
      </Section>

      <Section>
        <h2 className="text-3xl font-semibold md:text-4xl">Vanliga frågor</h2>
        <div className="mt-8 space-y-6 max-w-3xl">
          {FAQ.map((f) => (
            <div key={f.q}>
              <h3 className="text-lg font-medium">{f.q}</h3>
              <p className="mt-2 text-foreground/70">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <SignatureCta />
    </>
  );
}

